import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiError, shopApi, useShop } from "./ShopContext";

const Field = ({ label, testid, ...props }) => (
  <label className="block text-sm font-medium text-slate-700">{label}
    <input {...props} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none focus:border-[#D96B27]" data-testid={testid} />
  </label>
);

export default function ShopLogin() {
  const { signIn } = useShop();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/shop/account";
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const google = () => {
    sessionStorage.setItem("shop_after_login", next);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/shop/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (mode === "forgot") {
        const { data } = await shopApi.post("/auth/forgot-password", { email: form.email });
        toast.success(data.message);
        setMode("login");
        return;
      }
      const body = mode === "register" ? form : { email: form.email, password: form.password };
      const { data } = await shopApi.post(`/auth/${mode}`, body);
      signIn(data.token, data.customer);
      navigate(next, { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md rounded-2xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="shop-login-page">
      <h1 className="text-2xl font-extrabold text-slate-900">{mode === "register" ? "Create your account" : mode === "forgot" ? "Reset your password" : "Sign in"}</h1>
      <p className="mt-1 text-sm text-slate-500">Track orders and see your store account discount.</p>
      {mode !== "forgot" && (
        <>
          <button type="button" onClick={google} className="mt-6 flex w-full items-center justify-center gap-2 rounded-full border-2 border-slate-300 py-2.5 font-semibold text-slate-800 hover:bg-slate-50" data-testid="shop-google-login-btn">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" /> Continue with Google
          </button>
          <div className="my-5 flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />or with email<span className="h-px flex-1 bg-slate-200" /></div>
        </>
      )}
      <form onSubmit={submit} className="space-y-3">
        {mode === "register" && <Field label="Full name" value={form.name} onChange={upd("name")} required testid="shop-register-name" />}
        <Field label="Email" type="email" value={form.email} onChange={upd("email")} required testid="shop-login-email" />
        {mode !== "forgot" && <Field label="Password" type="password" value={form.password} onChange={upd("password")} required minLength={mode === "register" ? 8 : 1} testid="shop-login-password" />}
        {mode === "register" && <Field label="Cellphone" value={form.phone} onChange={upd("phone")} testid="shop-register-phone" />}
        {error && <p className="rounded-lg bg-rose-50 p-2 text-sm text-rose-700" data-testid="shop-login-error">{error}</p>}
        <button type="submit" disabled={busy} className="w-full rounded-full bg-[#D96B27] py-3 font-bold text-white hover:bg-[#C2591B] disabled:opacity-60" data-testid="shop-login-submit">
          {busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : mode === "register" ? "Create account" : mode === "forgot" ? "Send reset link" : "Sign in"}
        </button>
      </form>
      <div className="mt-4 flex justify-between text-sm">
        {mode === "login" ? (
          <>
            <button type="button" className="font-semibold text-[#D96B27]" onClick={() => setMode("register")} data-testid="shop-switch-register">Create account</button>
            <button type="button" className="text-slate-500" onClick={() => setMode("forgot")} data-testid="shop-switch-forgot">Forgot password?</button>
          </>
        ) : (
          <button type="button" className="font-semibold text-[#D96B27]" onClick={() => setMode("login")} data-testid="shop-switch-login">Back to sign in</button>
        )}
      </div>
      <p className="mt-6 text-center text-xs text-slate-400"><Link to="/shop">Continue shopping</Link></p>
    </div>
  );
}
